import { TableCell } from "@mui/material";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import React, { FC, useCallback, useEffect, useState } from "react";
import { MESSAGE_TYPE } from "../../../common/constants/common.constants";
import { IWithdrawalDataDashboard } from "../../../common/interfaces/user-dashboard.interface";
import {
  claimMultipleAmounts,
  withdrawTokensFromDashboard,
} from "../../../solana/common/helpers/user.dashboard.helpers";
import { createNotification } from "../../../utilities/notification";
import SkeletonItem from "../../SkeletonItem/SkeletonItem";
import { rowCellStyle } from "../DashboardTable/TableHeading/TableHeading.styles";
import "./DashboardClaimButton.scss";
import { dashboardStore } from "../../../contexts/dashboardStore";

const DashboardClaimButton: FC<{
  clubAddress: string;
  isTreasuryWithdrawal?: boolean;
  withdrawalAddress?: string;
}> = ({ clubAddress, isTreasuryWithdrawal, withdrawalAddress }) => {
  const { withdrawals, setWithdrawals } = dashboardStore();
  const wallet = useAnchorWallet();
  const [availableClaims, setAvailableClaims] =
    useState<IWithdrawalDataDashboard[]>();

  const [hasActiveClaim, toggleHasActiveClaim] = useState(false);

  useEffect(() => {
    const withdrawalInfo = withdrawals!.filter(
      (withdrawalData) =>
        withdrawalData.club.clubAddress.toString() === clubAddress &&
        !withdrawalData.hasWithdrawn
    );
    if (!isTreasuryWithdrawal) {
      const withdrawal = withdrawals?.find(
        (w) =>
          w.club.clubAddress === clubAddress &&
          w.withdrawal.toString() === withdrawalAddress
      );
      toggleHasActiveClaim(!withdrawal?.hasWithdrawn ?? false);
    } else {
      withdrawalInfo.length > 0
        ? toggleHasActiveClaim(true)
        : toggleHasActiveClaim(false);
    }
    setAvailableClaims(withdrawalInfo);
  }, [withdrawals]);

  const claimSingleAmount = useCallback(
    async (e: any) => {
      try {
        e.stopPropagation();
        const withdrawalInfo = withdrawals?.find(
          (w) => w.withdrawal.toString() === withdrawalAddress?.toString()
        );
        if (!withdrawalInfo) return;
        const addedWithdrawals = await withdrawTokensFromDashboard(
          withdrawalInfo,
          wallet!,
          withdrawals!
        );
        setWithdrawals(addedWithdrawals);
        createNotification(
          MESSAGE_TYPE.SUCCESS,
          "Tokens withdrawed successfully"
        );
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Problem with claiming",
          error.message
        );
      }
    },
    [withdrawals, hasActiveClaim]
  );

  const claimProposalAmount = useCallback(
    async (e: any) => {
      try {
        e.stopPropagation();

        if (!availableClaims) return;

        const availableClaimsOverZeroAmount = availableClaims.filter(
          (item) => item.withdrawalRecordAmount > 0
        );

        if (availableClaimsOverZeroAmount.length === 0) return;

        if (availableClaimsOverZeroAmount.length <= 1) {
          const addedWithdrawals = await withdrawTokensFromDashboard(
            availableClaimsOverZeroAmount[0]!,
            wallet!,
            withdrawals!
          );
          setWithdrawals(addedWithdrawals);
        } else {
          const updatedWithdrawals = await claimMultipleAmounts(
            availableClaimsOverZeroAmount,
            wallet!
          );
          const addedWithdrawals = [...withdrawals!];
          updatedWithdrawals.forEach((withdrawal) => {
            const withdrawalIndex = addedWithdrawals?.findIndex(
              (w) =>
                w.withdrawal.toString() === withdrawal.withdrawal.toString()
            );
            addedWithdrawals[withdrawalIndex] = { ...withdrawal };
          });
          setWithdrawals(addedWithdrawals);
        }
        createNotification(
          MESSAGE_TYPE.SUCCESS,
          "Tokens withdrawed successfully"
        );
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Problem with claiming",
          error.message
        );
      }
    },
    [withdrawals, wallet, availableClaims, hasActiveClaim]
  );

  return (
    <TableCell style={rowCellStyle}>
      {availableClaims ? (
        <button
          onClick={(e) => {
            isTreasuryWithdrawal
              ? claimProposalAmount(e)
              : claimSingleAmount(e);
          }}
          className={
            !hasActiveClaim
              ? "claim-button"
              : "claim-button claim-button--claim"
          }
        >
          {!hasActiveClaim ? "Claimed" : "Claim"}
        </button>
      ) : (
        <SkeletonItem width={47} height={20} borderRadius={5} />
      )}
    </TableCell>
  );
};

export default DashboardClaimButton;
